import * as React from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import {
  ClassicEditor,
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  Indent,
  IndentBlock,
  Italic,
  Link,
  Paragraph,
  RemoveFormat,
  SelectAll,
  ShowBlocks,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Underline,
  Undo
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import AutoTranslateButton from '../tools/AutoTranslateButton';

// Optional: Import Farsi locale if available
// import 'ckeditor5/translations/fa';

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';

export default function TissText({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
  const { t } = useTranslation();
  const cookie = getCookies();

  // Helper to get multilingual value
  const getMultilingualValue = (field: any, lang: LanguageCode, fallback: string = ''): string => {
    if (!field) return fallback;
    if (typeof field === 'string') return field; // Legacy support
    if (typeof field === 'object') {
      return field[lang] || field.en || field.fa || field.ku || field.ar || field.tr || fallback;
    }
    return fallback;
  };

  // Helper to set multilingual value
  const setMultilingualValue = (currentData: any, lang: LanguageCode, value: string): any => {
    if (!currentData || typeof currentData !== 'object') {
      return { [lang]: value };
    }
    return {
      ...currentData,
      [lang]: value
    };
  };

  // Get current HTML value for the active language
  const getCurrentHtmlForLang = () => {
    const htmlField = data?.data?.html || {};
    return getMultilingualValue(htmlField, activeLang);
  };

  const initialHtml = getCurrentHtmlForLang();

  // Initialize multilingual data from props
  const initializeMultilingualData = () => {
    const htmlField = data?.data?.html || {};
    if (typeof htmlField === 'object' && htmlField !== null && !Array.isArray(htmlField)) {
      return { ...htmlField };
    } else if (typeof htmlField === 'string' && htmlField) {
      return { en: htmlField };
    }
    return {};
  };

  // State - Store ALL multilingual data in state (all 5 languages at once)
  const [multilingualHtml, setMultilingualHtml] = React.useState<any>(initializeMultilingualData());

  // Get current language HTML from multilingual state
  const currentHtml = multilingualHtml[activeLang] || '';
  const hasContent = !!currentHtml || Object.values(multilingualHtml).some((val: any) => !!val);

  const [isEditing, setIsEditing] = React.useState(!hasContent);
  const [saving, setSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const prevLangRef = React.useRef(activeLang);
  const prevDataIdRef = React.useRef(data?._id);

  // Update multilingual state when data from server changes
  React.useEffect(() => {
    if (prevDataIdRef.current !== data?._id) {
      const htmlField = data?.data?.html || {};

      // Initialize multilingual data from server
      let newMultilingualData: any;
      if (typeof htmlField === 'object' && htmlField !== null && !Array.isArray(htmlField)) {
        // Merge with existing state to preserve languages that might not be in server response
        newMultilingualData = { ...multilingualHtml, ...htmlField };
      } else if (typeof htmlField === 'string' && htmlField) {
        // Legacy format - preserve existing languages, add this as 'en'
        newMultilingualData = { ...multilingualHtml, en: htmlField };
      } else {
        // Empty - keep existing multilingual data
        newMultilingualData = { ...multilingualHtml };
      }

      setMultilingualHtml(newMultilingualData);
      setHasChanges(false);
      prevDataIdRef.current = data?._id;
    }

    // Update ref when language changes
    if (prevLangRef.current !== activeLang) {
      setHasChanges(false);
      prevLangRef.current = activeLang;
    }
  }, [data?._id, data?.data?.html, activeLang]);

  // Track changes - compare editor content with multilingual state
  // This will be handled in handleEditorChange

  // Save handler - save ALL languages at once
  const saveContent = async () => {
    if (!data?._id) return;

    setSaving(true);
    try {
      console.log('=== SAVING ALL MULTILINGUAL HTML ===');
      console.log('Component ID:', data._id);
      console.log('Current language:', activeLang);
      console.log('All languages in state:', multilingualHtml);
      console.log('Languages count:', Object.keys(multilingualHtml).length);

      // Send ALL multilingual data to backend
      const dataToSend = {
        html: multilingualHtml // Send complete multilingual object
      };

      console.log('Sending to backend:', JSON.stringify(dataToSend, null, 2));

      const response = await axios.put(
        `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
        dataToSend,
        {
          headers: { Authorization: 'Bearer ' + cookie['token'] },
        }
      );

      console.log('Server response:', response.data);

      Swal.fire({
        title: t('Saved successfully'),
        icon: 'success',
        timer: 1200,
        showConfirmButton: false,
      });
      setIsEditing(false);
      setHasChanges(false);

      // Reload to get fresh data
      reload();
    } catch (error: any) {
      console.error('Save failed:', error);
      console.error('Error response:', error.response?.data);
      Swal.fire({
        title: t('Error saving'),
        text: error.response?.data?.message || error.message,
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Cancel handler with confirmation if there are changes
  const handleCancel = () => {
    if (hasChanges) {
      Swal.fire({
        title: t('Discard changes?'),
        text: t('You have unsaved changes. Are you sure you want to cancel?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: t('Yes, discard'),
        cancelButtonText: t('No, keep editing'),
      }).then((result) => {
        if (result.isConfirmed) {
          // Reload from server to reset all changes
          reload();
          setIsEditing(false);
          setHasChanges(false);
        }
      });
    } else {
      setIsEditing(false);
    }
  };

  // Editor config (memoized to prevent re-renders)
  const editorConfig = React.useMemo(() => ({
    language: 'fa',
    toolbar: {
      items: [
        'undo', 'redo', '|',
        'heading', '|',
        'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', '|',
        'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', '|',
        'alignment', '|',
        'numberedList', 'bulletedList', 'indent', 'outdent', '|',
        'link', 'blockQuote', 'insertTable', 'horizontalLine', 'specialCharacters', '|',
        'removeFormat', 'sourceEditing'
      ],
      shouldNotGroupWhenFull: true,
    },
    plugins: [
      AccessibilityHelp,
      Alignment,
      Autoformat,
      AutoLink,
      Autosave,
      BlockQuote,
      Bold,
      Code,
      Essentials,
      FindAndReplace,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      Indent,
      IndentBlock,
      Italic,
      Link,
      Paragraph,
      RemoveFormat,
      SelectAll,
      ShowBlocks,
      SourceEditing,
      SpecialCharacters,
      SpecialCharactersArrows,
      SpecialCharactersCurrency,
      SpecialCharactersEssentials,
      SpecialCharactersLatin,
      SpecialCharactersMathematical,
      SpecialCharactersText,
      Strikethrough,
      Style,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      Underline,
      Undo
    ],
    initialData: currentHtml,
    // Enable RTL
    ui: {
      viewportTopOffset: 0
    }
  }), [activeLang]); // Re-create config when language changes

  // Handle editor change - update ONLY the current language in multilingual state
  const handleEditorChange = (_: any, editor: any) => {
    const newHtml = editor.getData();

    // Update only the current language, preserve all others
    setMultilingualHtml((prev: any) => ({
      ...prev, // Keep all other languages
      [activeLang]: newHtml // Update only current language
    }));

    // Check if there are changes
    const originalHtml = data?.data?.html || {};
    const originalForLang = typeof originalHtml === 'object' && originalHtml !== null
      ? (originalHtml[activeLang] || '')
      : (activeLang === 'en' ? originalHtml : '');
    setHasChanges(newHtml !== originalForLang);
  };

  // Placeholder view (when no content)
  if (!hasContent && !isEditing) {
    return (
      <div
        className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
        onClick={() => setIsEditing(true)}
        dir="rtl"
      >
        <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
          <img src="/icons/addtext.svg" className="h-8 w-8" alt={t('Add Text')} />
        </div>
        <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
          {t('Click to add text')}
        </span>
        <span className="text-gray-400 text-xs mt-1">
          {t('Start typing to create content')}
        </span>
      </div>
    );
  }

  // Edit Mode
  if (isEditing) {
    return (
      <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
        {/* Editor Content */}
        <div className="p-0.5">
          <div className="overflow-hidden">
            <CKEditor
              key={`editor-${data?._id}-${activeLang}`}
              editor={ClassicEditor}
              // @ts-ignore
              config={editorConfig}
              data={currentHtml}
              onChange={handleEditorChange}
              onReady={(editor: any) => {
                // Store editor instance to prevent re-initialization issues
                console.log('CKEditor ready for language:', activeLang);
              }}
            />
          </div>

          {/* Action Buttons */}
          <div className="mt-4 flex gap-3 justify-between items-center">
            <div className="flex items-center gap-2">
              <AutoTranslateButton
                sourceText={multilingualHtml.en || ''}
                targetLang={activeLang}
                isHtml={true}
                onTranslated={(translatedHtml) => {
                  setMultilingualHtml((prev: any) => ({
                    ...prev,
                    [activeLang]: translatedHtml
                  }));
                  setHasChanges(true);
                }}
              />
              {activeLang !== 'en' && multilingualHtml.en && (
                <span className="text-xs text-gray-500">ترجمه از متن انگلیسی</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
                  {t('Unsaved changes')}
                </span>
              )}
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('Cancel')}
              </button>
              <button
                type="button"
                onClick={saveContent}
                disabled={saving || !hasChanges}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t('Saving...')}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('Save')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Preview Mode
  return (
    <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
      <div className="p-0.5 relative">
        {currentHtml ? (
          <div
            className="text-right prose prose-sm max-w-none prose-headings:font-semibold prose-p:text-gray-700 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: currentHtml }}
          />
        ) : (
          <div className="text-gray-400 italic py-8 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{t('متنی وجود ندارد')}</span>
            </div>
          </div>
        )}

        {/* Edit Button - Appears on hover */}
        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
          <button
            onClick={() => setIsEditing(true)}
            className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('ویرایش متن')}
          </button>
        </div>
      </div>
    </div>
  );
}